/* eslint-disable no-useless-concat */

import React, { useState, useEffect } from 'react';
import {
    makeStyles,
    Theme,
    createStyles,
    TextField,
    Typography,
    Box,
    Avatar,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { showNotification } from '../../App';
import { client } from '../..';
import { FetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';
import { fetchTypedUserSettings } from '../../userSettings/UserSettingsRepository';
import { MatterTerminology } from '../../MatterTerminology';
import { DocumentListFile } from '../../common/constants';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        dialogTitle: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
        titleIcon: {
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: '#e3f2fd',
            color: theme.palette.primary.main,
        },
        titleSubtext: {
            marginTop: 2,
        },
        serviceLevelsButton: {
            marginLeft: 'auto',
            textTransform: 'none',
            flexShrink: 0,
        },
        description: {
            marginBottom: theme.spacing(2),
        },
        textArea: {
            width: '100%',
            '& textarea': {
                fontSize: '15px',
                lineHeight: 1.5,
            },
        },
    })
);

interface SupportEmailState {
 
    note: string;
    userName: string;
    tenant: string;
    supportEmail: string;
}

// Explicit request: lets a caller reframe this same dialog/send-email flow for its own context
// (e.g. NotificationCenter.tsx's release-note "want to know more" button) without hardcoding
// that caller's wording here - each field falls back to this dialog's own default below when
// omitted, so the plain header "Get Support" button keeps its current wording unchanged.
export interface SupportDialogCustomization {
    title?: string;
    subtitle?: string;
    description?: string;
    /** handleSend's own email subject line - falls back to 'User Web Issue' when omitted. */
    subject?: string;
}

export interface SupportEmailProps {
    isAuthenticated: boolean;
    onClose: () => void;
    initialNote?: string;
    customization?: SupportDialogCustomization;
}

// tslint:disable-next-line: no-anyW
export const SupportEmailDialog: React.FC<SupportEmailProps> = ( props ) => {
    const classes = useStyles();

    const [state, setState] = useState<SupportEmailState>({
        note: props.initialNote || '',
        userName: '',
        tenant: '',
        supportEmail: ''
    });

    useEffect(() => {
        if (props.isAuthenticated) {
            fetchData();
        }
    // tslint:disable-next-line:align
    }, [props.isAuthenticated]);

    // tslint:disable-next-line:no-any
    const handleTextChange = (event: any) => {
        const note = event.target.value;
        setState((prevState) => {
            return {
                ...prevState,
                note: note 
            };
        });
    };
    const handleSend = () =>  {
        if (state.note !== '') {
            var body = GenerateEmailBody();
            var subject = props.customization?.subject ?? 'User Web Issue';
            var recipient = 'support@redraincorp.com';
            var cc = '';
            if (state.supportEmail !== recipient && state.supportEmail) {
                cc = '; ' + state.supportEmail;

            }

            var mailToString = 'mailTo:' + recipient + cc + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
            window.location.href = mailToString;
        }
        if (props.onClose) {
          return props.onClose();
        }
    };

    const GenerateEmailBody = () => {
        var body = 'User: ' + getUserName()
        + '\nTenant: ' + state.tenant
        + '\nApp: Connect Web'
        + '\nBrowser: ' + detectBrowser()
        // tslint:disable-next-line:no-string-literal
        // + '\nNetworkType: ' + navigator['connection'].effectiveType
        + '\nTimeZoneOffset: ' + new Date().getTimezoneOffset() / 60.0
        + (state.note ? '\n\n' + state.note : '');
        return body;
    };

    const getUserName = () =>  {
       return state.userName ? state.userName : 'Not logged in';
    };

    const detectBrowser = () =>  { 
        if ((navigator.userAgent.indexOf('Opera') || navigator.userAgent.indexOf('OPR')) !== -1 ) {
            return 'Opera';
        } else if (navigator.userAgent.indexOf('Chrome') !== -1 ) {
            return 'Chrome';
        } else if (navigator.userAgent.indexOf('Safari') !== -1) {
            return 'Safari';
        } else if (navigator.userAgent.indexOf('Firefox') !== -1 ) {
            return 'Firefox';
        } else if (navigator.userAgent.indexOf('MSIE') !== -1 ) {
            return 'IE';
        } else {
            return 'Unknown';
        }
    };

    const fetchData = () =>  {
        fetchTypedUserSettings(
            true,
            // tslint:disable-next-line:no-any
            (data: any) => { setState((prevState: SupportEmailState) => {
                return {
                    ...prevState,
                    supportEmail: data.timeslipContactInfo,
                };
            });
            }, 
            reason => {
              showNotification(
                'Failed to Fetch Layout Configuration',
                reason,
                'error'
              );
            }
          );
        fetchUserData(false, 
                      // tslint:disable-next-line:no-any
                      (data: any) => {  setState((prevState: SupportEmailState) => {
                            return {
                                ...prevState,
                                userName: data.appUser ? data.appUser.userName : '',
                                tenant:  data.tenant.tenantId
                            };
                        });
                      }, 
                      (reason) => {   
                          showNotification('Failed to Matter Summary New Enabled Lookup value', reason, 'error'); 
                      });
    };

    const onServiceLevel = () => {
        window.open(DocumentListFile.ServiceLevel, '_blank');
    };

    return (
        <React.Fragment>
            <Dialog
                open={true}
                onClose={() => props.onClose()}
                className={classes.root}
                maxWidth="md"
                fullWidth={true}
            >
                <DialogTitle
                    id="form-dialog-title"
                    disableTypography={true}
                    className={classes.dialogTitle}
                >
                    <Avatar className={classes.titleIcon}>
                        <HelpOutlineIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6">{props.customization?.title ?? 'Request Support'}</Typography>
                        <Typography variant="body2" color="textSecondary" className={classes.titleSubtext}>
                            {props.customization?.subtitle ?? 'We\'ll open your default email app with the details below'}
                        </Typography>
                    </Box>
                    <Button
                        color="primary"
                        onClick={onServiceLevel}
                        className={classes.serviceLevelsButton}
                    >
                        {MatterTerminology.ServiceLevels}
                    </Button>
                </DialogTitle>
                <DialogContent dividers={true}>
                    <Typography variant="body2" color="textSecondary" className={classes.description}>
                        {props.customization?.description ?? (
                            'If you are reporting on an issue or an error please provide as much specific detail as possible to the nature of the problem. '
                            + 'Screen shots are often very useful. When you click SEND, an email will be created in your default email application. '
                            + 'Any attachments or screenshots can be added on that screen.'
                        )}
                    </Typography>
                    <TextField
                        className={classes.textArea}
                        id="outlined-multiline-static"
                        label="Describe your issue"
                        placeholder="What happened, and what did you expect instead?"
                        variant="outlined"
                        multiline={true}
                        rows={14}
                        value={state.note}
                        onChange={handleTextChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={handleSend}
                    >
                        Send
                    </Button>
                    <Button
                        onClick={() => props.onClose()}
                        color="primary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export function fetchUserData(refreshData: boolean,
                                // tslint:disable-next-line:no-any
                              onSuccess: (data?: any) => void,
                                // tslint:disable-next-line:no-any
                              onError: (reason: any) => void)
    : void {
        var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';

        client.query({
            query: userInfoQuery,
            variables: {},
            fetchPolicy: fetchPolicy
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any; }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
            .catch((reason: any ) => { onError(reason); }
        );
}

const userInfoQuery = gql`query user {
    appUser {
      userName
    }
    tenant {
      tenantId
    }
  }
  `;
